import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PaginationService {

  constructor(private readonly translate: TranslateService) {}

  private readonly _result = new BehaviorSubject<any[]>(undefined);
  private readonly _allResult = new BehaviorSubject<any[]>(undefined);
  private readonly _pages = new BehaviorSubject<any[]>(undefined);
  private readonly _pageActuelReload = new BehaviorSubject<number>(undefined);

  data: any[] = [];
  afficherPerPage = 10;
  pageActuel = 1;
  idNational: string;
  structure: string;
  nom: string;
  contrat: string;
  habilitation: string;
  detailStructure: string;
  raisonSociale: string;
  commune: string;
  structureHabilitee: string;
  structureBeneficiaire: string;
  habilitationPMType = 'HabilitationPM';

  changeAfficherPerPage(nbItem: number) {
    this.afficherPerPage = nbItem;
    this._pageActuelReload.next(1);
    this.calculeData();
    this.changeNumberOfPages();
  }

  calculeData(option?: string, type?: string) {
    this.configVariables().subscribe(() => {
      switch (type) {
        case this.habilitation:
          this.orderHabilitations(option);
          break;
        case this.habilitationPMType:
          this.orderHabilitationsPM(option);
          break;
        case this.structure:
          this.orderStructures(option);
          break;
        case this.contrat:
          this.orderContrats(option);
          break;
        case this.detailStructure:
          this.orderDetailStructure(option);
          break;
        default: {
          if (this.data?.length > 0) {
            this.sortWithoutType(option);
          }
        }
      }
      const pageActuel = this.pageActuel;
      const init = this.afficherPerPage * (pageActuel - 1);
      const end = this.afficherPerPage * pageActuel;

      this._allResult.next(this.data);

      if (this.data.length < this.afficherPerPage) {
        this._result.next(this.data);
      } else {
        this._result.next(this.data.slice(init, end));
      }
      this.changeNumberOfPages();
    });
  }

  sortWithoutType(option: string) {
    switch (option) {
      case this.nom: {
        this.data.sort((a, b) => a.nom?.localeCompare(b.nom));
        break;
      }
      case this.structure: {
        this.data.sort((a, b) =>
          a.raisonSocialeStructure?.localeCompare(b.raisonSocialeStructure)
        );
        break;
      }
      case this.raisonSociale: {
        this.data.sort((a, b) =>
          a.raisonSociale?.localeCompare(b.raisonSociale)
        );
        break;
      }
      case this.commune: {
        this.data.sort((a, b) =>
          a.commune?.localeCompare(b.commune)
        );
        break;
      }
      case this.idNational: {
        this.data.sort((a, b) => {
          const typeStructure = a.typeStructure?.localeCompare(b.typeStructure);
          if (typeStructure !== 0) {
            return typeStructure;
          }
          return a.idStructure?.localeCompare(b.idStructure);
        });
        break;
      }
    }
  }

  orderContrats(option: string) {
    if (option === this.contrat) {
      this.data.sort((a, b) => a.numeroContrat?.localeCompare(b.numeroContrat));
    }
    if (option === this.structure) {
      this.data.sort((a, b) =>
        a.structureAbonne?.localeCompare(b.structureAbonne)
      );
    }
  }

  orderStructures(option: string) {
    if (option === this.idNational) {
      this.data.sort(
        (a, b) =>
          b.abonne - a.abonne ||
          `${a.typeStructure}${a.idStructure}`.localeCompare(
            `${b.typeStructure}${b.idStructure}`
          )
      );
    }
    if (option === this.structure && this.data) {
      this.data.sort((a, b) => a.raisonSociale?.localeCompare(b.raisonSociale));
    }
  }

  orderHabilitations(option: string) {
    if (option === this.nom) {
      this.data.sort((a, b) =>
        a.personneHabilitee?.nom?.localeCompare(b.personneHabilitee?.nom)
      );
    }
    if (option === this.structure && this.data) {
      this.data.sort((a, b) =>
        a.structure?.raisonSociale?.localeCompare(b.structure?.raisonSociale)
      );
    }
  }

  orderHabilitationsPM(option: string) {
    const structure = (!option || option === this.structureBeneficiaire) ? 'structure' : (option === this.structureHabilitee) ? 'structureHabilitee': undefined;

    if (!structure) {
      return;
    }
    if(this.data) {
      this.data.sort((a, b) => a[structure]?.raisonSociale?.localeCompare(b[structure]?.raisonSociale));
    }
  }

  orderDetailStructure(option: string) {
    if (option === this.nom) {
      this.data.sort((a, b) =>
        a.personneHabilitee?.nom?.localeCompare(b.personneHabilitee?.nom)
      );
    }
    if (option === this.idNational) {
      this.data.sort((a, b) =>
        a.personneHabilitee?.typePersonne?.concat(a.personneHabilitee.idPersonne)
          .localeCompare(
            b.personneHabilitee?.typePersonne?.concat(
              b.personneHabilitee.idPersonne
            )
          )
      );
    }
  }
  resetPageActuel(): void { 
    this.pageActuel = 1; 
    this._pageActuelReload.next(1); 
  }
  setData(list: any[], option?: string, type?: string, resetPage = false) {
    
    if (resetPage) { 
      this.resetPageActuel(); 
    } 
    this.data = list ?? []; 
    this.calculeData(option, type); 
  }

  changePageActuel(pageActuelle: number) {
    this.pageActuel = pageActuelle;
    this.calculeData();
  }

  changeNumberOfPages() {
    const pages =
      this.data && this.data.length > this.afficherPerPage
        ? Math.floor(
            (Number(this.data.length) + Number(this.afficherPerPage) - 1) /
              Number(this.afficherPerPage)
          )
        : 1;
    this._pages.next([...Array(pages).keys()]);
  }

  configVariables(): Observable<void> {
    return this.translate.get('last').pipe(
      map(() => {
        this.idNational = this.translate.instant('trier.idNational');
        this.nom = this.translate.instant('trier.nom');
        this.structure = this.translate.instant('trier.structure');
        this.contrat = this.translate.instant('trier.contrat');
        this.habilitation = this.translate.instant('trier.habilitation');
        this.detailStructure = this.translate.instant('trier.detailStructure');
        this.raisonSociale = this.translate.instant('trier.raisonSociale');
        this.commune = this.translate.instant('trier.commune');
        this.structureHabilitee = this.translate.instant('trier.structureHabilitee');
        this.structureBeneficiaire = this.translate.instant('trier.structureBeneficiaire');
      })
    );
  }

  get result(): any {
    return this._result;
  }

  get allResult(): any {
    return this._allResult;
  }

  get pages(): any {
    return this._pages;
  }

  get pageActuelReload(): any {
    return this._pageActuelReload;
  }

}
